import { Strategy } from '@interfaces/financialProduct';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useGlobalToast } from '@providers/globalToastProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { calculateDurationSeconds } from '@utils/analytics/eventMetrics';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio.types';
import { assignSecurityColors } from '../utils/assignSecurityColors';
import { normalizeCashSecurity } from '../utils/normalizeCashSecurity';
import { useCsvSecuritiesImport } from '../utils/useCsvSecuritiesImport';
import { useModelPortfolioCash } from '../utils/useModelPortfolioCash';
import { useSaveModelPortfolio } from '../utils/useSaveModelPortfolio';
import { AddModelPortfolioSecuritiesVisual } from './addModelPortfolioSecurities.visual';
import type { AvailableSecurity } from './components/securitiesReviewDialog';

const DEFAULT_COLOR = 'supportOne' as const;
const TEMP_ID_PREFIX = 'temp-';

export const AddModelPortfolioSecurities = ({
  options,
  onNext,
  workflowCompletion,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
}) => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { showToast } = useGlobalToast();
  const { t } = useTranslation(['buildModels']);
  const { workflowData, setWorkflowData, updateWorkflowContext } =
    useContext(WorkflowContext);
  const [stepStartTime, setStepStartTime] = useState<Date | null>(null);
  const [modelPortfolioDetails, setModelPortfolioDetails] =
    useState<ModelPortfolioData>(workflowData.modelPortfolioDetails);
  const [securities, setSecurities] = useState<Security[]>(
    modelPortfolioDetails?.securities || [],
  );
  const { saveModelPortfolio, loading } = useSaveModelPortfolio();
  const [showImportPrompt, setShowImportPrompt] = useState(false);

  const updateSecuritiesState = useCallback((newSecurities: Security[]) => {
    setSecurities(newSecurities);
    setModelPortfolioDetails((prev) => ({
      ...prev,
      securities: newSecurities,
    }));
  }, []);

  const isAssetClassStrategy =
    modelPortfolioDetails.strategy === Strategy.ASSET_CLASS;

  const stableAssetClassBranches = useMemo(
    () => modelPortfolioDetails.assetClassBranches ?? [],
    [modelPortfolioDetails.assetClassBranches],
  );

  const { hasCashBranch, cashLoading, cashProduct, cashError } =
    useModelPortfolioCash(
      stableAssetClassBranches,
      modelPortfolioDetails.assetClassLevel,
    );

  useEffect(() => {
    if (cashError) {
      showToast({
        severity: 'error',
        message: t('cashFetchError'),
      });
    }
  }, [cashError, showToast, t]);

  const onCsvImportComplete = useCallback(
    (imported: Security[]) => {
      updateSecuritiesState(
        imported.map((s) => normalizeCashSecurity(s, cashProduct)),
      );
    },
    [updateSecuritiesState, cashProduct],
  );

  const {
    importLoading,
    showReviewDialog,
    unrecognizedSecurities,
    pendingImportData,
    handleImportFromCsv,
    handleReviewSubmit: handleReviewSubmitFromHook,
    handleReviewClose,
  } = useCsvSecuritiesImport({
    existingSecurities: securities,
    onImportComplete: onCsvImportComplete,
    isAssetClassStrategy,
  });

  useEffect(() => {
    setStepStartTime(new Date());
  }, []);

  useEffect(() => {
    setWorkflowData((prevWorkflowData) => ({
      ...prevWorkflowData,
      modelPortfolioDetails: {
        ...modelPortfolioDetails,
        securities,
      },
    }));
  }, [securities, modelPortfolioDetails, setWorkflowData]);

  useEffect(() => {
    if (securities.length === 0) {
      setShowImportPrompt(true);
    }
  }, []);

  const handleAddEmptySecurity = () => {
    const nextTaxRank =
      securities.length > 0
        ? Math.max(...securities.map((s) => s.taxRank ?? 0)) + 1
        : 1;

    const tempId = `${TEMP_ID_PREFIX}${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newSecurity: Security = {
      financialProduct: {
        id: tempId,
        name: '',
        primaryAssetClass: undefined,
        translatedName: getLanguageDefaults(),
      },
      isCashEquivalent: false,
      taxRank: nextTaxRank,
      percentage: 0,
      id: tempId,
      color: DEFAULT_COLOR,
    };
    updateSecuritiesState([...securities, newSecurity]);
  };

  const handleAddCash = () => {
    if (cashLoading) return;
    if (securities.some((s) => s.financialProduct.isCash)) return;

    if (!cashProduct) {
      showToast({
        severity: 'error',
        message: t('buildModels:noCashAssetClassFound'),
      });
      return;
    }

    // cashProduct.primaryAssetClass (or secondary/tertiary based on assetClassLevel)
    // is sourced from the DB so the visual's groupedSecurities filter can correctly
    // match this security to its asset class branch
    const newCashSecurity: Security = {
      financialProduct: cashProduct,
      percentage: 0,
      isCashEquivalent: false,
      taxRank: undefined,
      id: `${TEMP_ID_PREFIX}cash-${cashProduct.id}`,
      color: DEFAULT_COLOR,
    };

    updateSecuritiesState([...securities, newCashSecurity]);
  };

  const handleUpdateSecurity = (index: number, security: Security) => {
    const updated = [...securities];
    const current = updated[index];
    let next = { ...security };

    if (
      current.financialProduct.id?.startsWith(TEMP_ID_PREFIX) &&
      !security.financialProduct.id
    ) {
      next.financialProduct.id = current.financialProduct.id;
      next.id = current.financialProduct.id;
    }

    next = normalizeCashSecurity(next, cashProduct);

    updated[index] = next;
    updateSecuritiesState(assignSecurityColors(updated));
  };

  // Ref avoids adding `securities` to the effect deps below (stale-closure guard).
  const securitiesRef = useRef(securities);
  securitiesRef.current = securities;

  // Tracks the last cashProduct.id normalised so the effect is idempotent
  // across re-renders where cashProduct gets a new object reference.
  const normalizedForCashIdRef = useRef<string | undefined>(undefined);

  // Re-normalises cash securities already in state when cashProduct resolves
  // (handles the race where saved state loads before the query returns).
  useEffect(() => {
    if (!cashProduct) return;
    if (normalizedForCashIdRef.current === cashProduct.id) return;
    const hasCashSecurity = securitiesRef.current.some(
      (s) =>
        s.financialProduct.isCash || s.financialProduct.id === cashProduct.id,
    );
    if (!hasCashSecurity) return;
    normalizedForCashIdRef.current = cashProduct.id;
    updateSecuritiesState(
      securitiesRef.current.map((s) => normalizeCashSecurity(s, cashProduct)),
    );
  }, [cashProduct, updateSecuritiesState]);

  const handleRemoveSecurity = (index: number) => {
    updateSecuritiesState(securities.filter((_, i) => i !== index));
  };

  const handleUpdateTaxRank = (index: number, value: number) => {
    const updated = [...securities];
    updated[index] = { ...updated[index], taxRank: value };
    updateSecuritiesState(updated);
  };

  const handleUpdateTargetPercentage = (index: number, value: number) => {
    const updated = [...securities];
    updated[index] = { ...updated[index], percentage: value };
    updateSecuritiesState(updated);
  };

  const handleReorderSecurities = (reordered: Security[]) => {
    const isCashSecurity = (s: Security) =>
      s.financialProduct.isCash ||
      (cashProduct != null && s.financialProduct.id === cashProduct.id);

    const cashSecurities = reordered.filter(isCashSecurity);
    const nonCashSecurities = reordered.filter((s) => !isCashSecurity(s));

    const updated = [...nonCashSecurities, ...cashSecurities].map((s, i) => {
      const normalized = normalizeCashSecurity(s, cashProduct);
      return {
        ...normalized,
        financialProduct: { ...normalized.financialProduct },
        id: normalized.id || normalized.financialProduct.id,
        // Honour an explicit taxRank already set by the caller (e.g. drag-and-drop
        // pre-assigns i+1; non-reordering callers preserve the original value).
        // Fall back to positional rank only when no taxRank is provided.
        taxRank: isCashSecurity(s) ? undefined : (s.taxRank ?? i + 1),
      };
    });

    updateSecuritiesState(updated);
  };

  const continueFunc = async () => {
    await setWorkflowData({
      ...workflowData,
      modelPortfolioDetails: {
        ...workflowData.modelPortfolioDetails,
        securities,
      },
    });

    if (updateWorkflowContext) await updateWorkflowContext();

    const stepEndTime = new Date();
    const formDuration = calculateDurationSeconds(stepStartTime, {
      endTime: stepEndTime,
    });

    sendAnalytic(ovAnalyticsEvents.modelPortfolioWorkflowStep3Complete, {
      stepNumber: 3,
      stepName: 'ADD_MODEL_PORTFOLIO_SECURITIES',
      formDuration,
      timestamp: stepEndTime.toISOString(),
      workflowCompletionId: workflowCompletion?.id,
    });

    onNext();
  };

  const handleSaveForLater = async () => {
    const savedData = await saveModelPortfolio(modelPortfolioDetails);
    setModelPortfolioDetails({ ...savedData, originalValues: savedData });
  };

  const handleReviewSubmit = async (replacements: Record<string, string>) => {
    try {
      await handleReviewSubmitFromHook(replacements);
    } catch (error) {
      showToast({
        severity: 'error',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  return (
    <AddModelPortfolioSecuritiesVisual
      options={options}
      workflowCompletion={workflowCompletion}
      securities={securities}
      continueFunc={continueFunc}
      onAddSecurity={handleAddEmptySecurity}
      onAddCash={
        isAssetClassStrategy && (hasCashBranch || cashLoading)
          ? handleAddCash
          : undefined
      }
      onUpdateSecurity={handleUpdateSecurity}
      onRemoveSecurity={handleRemoveSecurity}
      onUpdateTaxRank={handleUpdateTaxRank}
      onUpdateTargetPercentage={handleUpdateTargetPercentage}
      onSaveForLater={handleSaveForLater}
      onImportFromCsv={handleImportFromCsv}
      onReorderSecurities={handleReorderSecurities}
      modelPortfolioDetails={modelPortfolioDetails}
      loading={loading || cashLoading}
      importLoading={importLoading}
      showImportPrompt={showImportPrompt}
      onCloseImportPrompt={() => setShowImportPrompt(false)}
      showReviewDialog={showReviewDialog}
      unrecognizedSecurities={unrecognizedSecurities}
      availableSecurities={
        (pendingImportData?.availableSecurities ?? []) as AvailableSecurity[]
      }
      onReviewSubmit={handleReviewSubmit}
      onReviewClose={handleReviewClose}
    />
  );
};

export default AddModelPortfolioSecurities;
